import { Box, Card, Chip, Dialog, Button as MUIButton } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import { ReactElement, useEffect, useState } from 'react';
import {
  ArrayField,
  BulkDeleteButton,
  Button,
  ChipField,
  CreateButton,
  Datagrid,
  DateField,
  DeleteButton,
  EditButton,
  email,
  Filter,
  FunctionField,
  List,
  minLength,
  PasswordInput,
  required,
  SelectInput,
  SingleFieldList,
  TextField,
  useGetIdentity,
  useListController,
  useNotify,
  usePermissions,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { CustomBreadcrumbs } from '../../../../components/custom-breadcrumbs';
import { Iconify } from '../../../../components/iconify';
import { DashboardContent } from '../../../../layouts/dashboard';
import {
  CgCreate,
  CgCreateProps,
  CgEdit,
  CgEditProps,
  SelectListAgencyInput,
} from '../../components/common';
import FilterGroup from '../../components/FilterGroup';
import XDatagrid from '../../components/XDatagrid';
import { XDateInput } from '../../components/XDateInput';
import XFilterAgency from '../../components/XFilterAgency';
import { SelectUserGroupInput } from '../../components/XInput';
import { XList } from '../../components/XList';
import XTextInput from '../../components/XTextInput';
import { Resources } from '../resource';
import { name } from './index';
import AssignRolePopup from './AssignRole';
import ResetPasswordPopup from './ResetPasswordPopup';
import ResetTwoFactorPopup from './ResetTwoFactorPopup';
import { PermissionData } from '../../entities/permission.entity';
import { hasPermission } from '../../utils/has-permission';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { useAuthProviders } from 'src/hooks/use-auth-providers';
import ErrorPage403 from 'src/pages/error/403';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import { XButton } from '../../components/XButton';
import UserForm from '../../components/User/UserForm';
import UserSessionsPopup from './UserSessionsPopup';

const ListFilter = (props: any) => {
  const { filterValues } = useListController();

  return (
    <Filter {...(props || {})}>
      <FilterGroup alwaysOn spacing={0} p={0}>
        <FilterGroup>
          <XTextInput source="q" label="module.user.username" />
          <XFilterAgency source="agencyIds" label="module.user.agency" multiple alwaysOn />
          {/* <SelectUserGroupInput
            source="groupId"
            label="module.user.group"
            multiple={false}
            alwaysOn
          /> */}
          <XDateInput label="resource.registeredFrom" source="fromTime" sx={{ width: 200 }} />
          <XDateInput
            label="resource.registeredTo"
            source="toTime"
            sx={{ width: 200 }}
            type="to"
            minDate={filterValues?.fromTime ? dayjs(filterValues.fromTime) : undefined}
          />
        </FilterGroup>
      </FilterGroup>
    </Filter>
  );
};

interface IUserFormProps {
  isCreate: boolean;
}
// const UserForm = (props: IUserFormProps) => {
//   const { isCreate } = props;
//   const translate = useTranslate();
//   const { watch } = useFormContext();
//   const record = useRecordContext();
//   const groupId = isCreate ? watch('groupId') : record?.groups?.[0];
//   const isShowAgencyField = ['agency', 'agency_operator'].includes(groupId);

//   return (
//     <Box sx={{ pt: 3, pb: 3 }}>
//       <Stack spacing={3}>
//         <XTextInput
//           source="username"
//           label="module.user.username"
//           validate={[required()]}
//           disabled={!props.isCreate}
//         />
//         <XTextInput
//           source="email"
//           label="module.user.email"
//           validate={[required(), email()]}
//           disabled={!props.isCreate}
//         />

//         {/* <SelectUserGroupInput
//           source={props.isCreate ? 'groupId' : 'groups'}
//           label="module.user.group"
//           multiple={false}
//           validate={[required()]}
//           disabled={!props.isCreate}
//         /> */}

//         {isShowAgencyField && (
//           <SelectListAgencyInput
//             source="agencies"
//             label="module.user.agency"
//             multiple={false}
//             validate={[required()]}
//             format={(value) => {
//               if (Array.isArray(value) && value.length > 0) {
//                 return value[0];
//               }

//               return value;
//             }}
//           />
//         )}

//         <PasswordInput
//           source="password"
//           label="module.user.password"
//           validate={props.isCreate ? [required(), minLength(8)] : [minLength(8)]}
//         />
//         <SelectInput
//           source="isVerified"
//           label="module.user.isVerified"
//           choices={[
//             { id: true, name: 'Yes' },
//             { id: false, name: 'No' },
//           ]}
//           validate={[required()]}
//         />
//       </Stack>
//     </Box>
//   );
// };

const EditPopup = (props: CgEditProps) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const t = useTranslate();

  const onSuccess = () => {
    const currentUrl = window.location.href;
    const origin = window.location.origin;
    const redirectValue = currentUrl.replace(origin, '');

    notify(t('module.agency.validate.updateSuccess'), { type: 'success', autoHideDuration: 1000 });

    if (props.onClose) {
      props.onClose();
    }
    if (props.resource) {
      redirect(redirectValue, props.resource);
    }
  };

  const onError = (error: any) => {
    const errorCode = error?.body?.code;
    const notifyError = (msg: string) => {
      notify(msg, { type: 'error' });
    };

    switch (errorCode) {
      case 'USER_EXISTED':
      case 'Email or username already exists':
        notifyError(t('module.user.validate.conflict'));
        break;
      default:
        notifyError(error?.body?.message || t('module.agency.validate.unknownError'));
        break;
    }
  };

  return (
    <CgEdit
      {...props}
      title="module.user.editUser"
      resource={Resources.users}
      onClose={props.onClose}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess,
        onError,
      }}
      transform={(data) => {
        // Passwords are changed through the Reset password action only; the server rejects the field here
        const { password: _password, ...rest } = data ?? {};
        const agencyData = rest?.agencies;
        if (!agencyData || typeof agencyData === 'string') {
          return rest;
        }

        const agencyArr = Array.from(new Set(agencyData ?? []));
        const agencyString: string = agencyArr.toString();
        return {
          ...rest,
          agencies: agencyString,
        };
      }}
    >
      <UserForm isCreate={false} />
    </CgEdit>
  );
};

const CreatePopup = (props: CgCreateProps) => {
  const redirect = useRedirect();
  const { password: passwordLoginEnabled } = useAuthProviders();
  const notify = useNotify();
  const t = useTranslate();

  const onSuccess = () => {
    const currentUrl = window.location.href;
    const origin = window.location.origin;
    const redirectValue = currentUrl.replace(origin, '');

    notify(t('module.agency.validate.updateSuccess'), { type: 'success', autoHideDuration: 1000 });

    if (props.onClose) {
      props.onClose();
    }
    if (props.resource) {
      redirect(redirectValue, props.resource);
    }
  };

  const onError = (error: any) => {
    const errorCode = error?.body?.code;
    const notifyError = (msg: string) => {
      notify(msg, { type: 'error' });
    };

    switch (errorCode) {
      case 'USER_EXISTED':
      case 'Email or username already exists':
        notifyError(t('module.user.validate.conflict'));
        break;
      case 'USERNAME_RESERVED':
        notifyError(error?.body?.message);
        break;
      default:
        notifyError(t('module.agency.validate.unknownError'));
        break;
    }
  };

  return (
    <CgCreate
      {...props}
      title="module.user.createUser"
      resource={Resources.users}
      mutationOptions={{
        onSuccess,
        onError,
      }}
      transform={(formData) => {
        // The service refuses a password while password sign-in is off (XBIT-4719): never send one
        let data = formData;
        if (!passwordLoginEnabled && data && 'password' in data) {
          const { password: _password, ...rest } = data;
          data = rest;
        }

        const agencyData = data?.agencies;
        if (!agencyData || typeof agencyData === 'string') {
          return data;
        }

        const agencyArr = Array.from(new Set(agencyData ?? []));
        const agencyString: string = agencyArr.toString();
        return {
          ...data,
          agencies: agencyString,
        };
      }}
    >
      <UserForm isCreate />
    </CgCreate>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const { password: passwordLoginEnabled } = useAuthProviders();
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const { data: identity } = useGetIdentity();

  return (
    <PermissionGuard resource="users" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.user.title')}
          links={[
            { name: translate('resource.users'), href: `/${Resources.users}` },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="users" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreatePopup onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({ alias: 'Proxy' });
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.users')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />
        <Card sx={{ mt: 3, pb: 2 }}>
          <List filters={<ListFilter />} exporter={false} actions={false} empty={false}>
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                isRowSelectable={(record: any) => !record?.isSuperAdmin}
                bulkActionButtons={
                  <PermissionGuard resource="users" action="delete" fallback={<></>}>
                    <BulkDeleteButton />
                  </PermissionGuard>
                }
                sx={{
                  '.RaBulkActionsToolbar-toolbar': {
                    position: 'relative !important',
                    transform: 'none !important',
                  },
                }}
              >
                <TextField source="username" label="module.user.username" />
                <TextField source="email" label="module.user.email" />
                <TextField source="name" label="module.user.fullName" />
                {/* <TextField source="isVerified" label="module.user.isVerified" minWidth={100} /> */}
                <FunctionField
                  label="module.user.isVerified"
                  render={(record) => (
                    <Chip
                      label={record.isVerified ? 'True' : 'False'}
                      color={record.isVerified ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />
                {/* <TextField source="agencyNames" label="module.user.agency" /> */}
                {/* <FunctionField
                label="module.user.group"
                textAlign="left"
                minWidth={150}
                render={(record: any) => {
                  return record.groups?.map((group: any) => group.name).join(', ');
                }}
              /> */}
                <DateField
                  source="registrationDate"
                  label="module.user.registeredAt"
                  options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                />
                {/* <FunctionField
                label="module.user.registeredAt"
                textAlign="left"
                minWidth={150}
                render={(record: any) => {
                  return dayjs(record.registrationDate).format('YYYY-MM-DD');
                }}
              /> */}

                <FunctionField
                  textAlign="left"
                  render={(record: any) => (
                    <Stack direction="row" spacing={3}>
                      {/* The backend refuses changes to the caller's own account and to the super-admin account; hide the actions it would refuse */}
                      {!(identity?.id && record.id === identity.id) && !record.isSuperAdmin && (
                        <>
                          <PermissionGuard resource="users" action="read-sessions" fallback={<></>}>
                            <Button
                              onClick={() =>
                                setPopup(
                                  <UserSessionsPopup
                                    id={record.id}
                                    username={record.username}
                                    onClose={() => setPopup(undefined)}
                                  />
                                )
                              }
                              variant="text"
                              size="small"
                              color="secondary"
                              label={translate('module.session.sessions')}
                            >
                              <Iconify icon="solar:devices-bold" />
                            </Button>
                          </PermissionGuard>
                          <PermissionGuard
                            anyOf={[
                              { resource: 'users', action: 'assign-role' },
                              { resource: 'users', action: 'assign-permission' },
                            ]}
                            fallback={<></>}
                          >
                            <Button
                              onClick={() =>
                                setPopup(
                                  <AssignRolePopup id={record.id} onClose={() => setPopup(undefined)} />
                                )
                              }
                              variant="text"
                              size="small"
                              color="secondary"
                              label={translate('resource.roles')}
                            >
                              <Iconify icon="eva:settings-2-fill" />
                            </Button>
                          </PermissionGuard>
                          <PermissionGuard resource="users" action="update" fallback={<></>}>
                            <EditButton
                              onClick={(e) => {
                                e.preventDefault();
                                setPopup(
                                  <EditPopup id={record.id} onClose={() => setPopup(undefined)} />
                                );
                              }}
                            />
                          </PermissionGuard>
                          <PermissionGuard resource="users" action="delete" fallback={<></>}>
                            <DeleteButton mutationMode="pessimistic" />
                          </PermissionGuard>
                          {passwordLoginEnabled && (
                            <PermissionGuard resource="users" action="reset-password" fallback={<></>}>
                              <Button
                                onClick={() =>
                                  setPopup(
                                    <ResetPasswordPopup
                                      id={record.id}
                                      username={record.username}
                                      onClose={() => setPopup(undefined)}
                                    />
                                  )
                                }
                                variant="text"
                                size="small"
                                color="secondary"
                                label={translate('module.user.resetPassword')}
                              >
                                <Iconify icon="solar:lock-bold" />
                              </Button>
                            </PermissionGuard>
                          )}
                          <PermissionGuard resource="users" action="reset-2fa" fallback={<></>}>
                            <Button
                              onClick={() =>
                                setPopup(
                                  <ResetTwoFactorPopup
                                    id={record.id}
                                    username={record.username}
                                    onClose={() => setPopup(undefined)}
                                  />
                                )
                              }
                              variant="text"
                              size="small"
                              color="secondary"
                              label={translate('module.user.resetTwoFactor')}
                            >
                              <Iconify icon="solar:shield-cross-bold" />
                            </Button>
                          </PermissionGuard>
                        </>
                      )}
                      <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                        <XButton
                          variant="text"
                          size="medium"
                          color="secondary"
                          disabled={!record.hasAuditLog}
                          onClick={() => {
                            setSelectedRecord(record);
                            setAuditTitle(`${translate('resource.auditLogs')} - (#${record.id})`);
                            setAuditLogOpen(true);
                          }}
                          label={translate('resource.auditLogs')}
                        >
                          <Iconify icon="ph:note" />
                        </XButton>
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          resource={Resources.users}
          recordId={selectedRecord?.id}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
